import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRight,
    BarChart3,
    Building2,
    CheckCircle2,
    Coins,
    FileSpreadsheet,
    FileText,
    MapPin,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import Header from "../components/HeaderV2";
import { KioskType, useKiosk } from "../KioskContext";
import { MemberType, useMember } from "../MemberContext";
import { PartnerType, usePartner } from "../PartnerContext";
import { PointType, usePoint } from "../PointContext";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/screens/DashboardV2.module.css";

const CARBON_REDUCTION_GRAM_PER_INPUT = 66;
const CAPACITY_WARNING_RATE = 50;
const CAPACITY_DANGER_RATE = 80;
type KioskDisplayStatus = KioskType["status"] | "NEAR_FULL";

const DashboardV2 = () => {
    const { kiosks, pendings } = useKiosk();
    const { members } = useMember();
    const { partner } = usePartner();
    const { points, withdraws } = usePoint();

    const now = Date.now();
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);
    const sevenDaysAgo = startOfDay(now - 6 * 24 * 60 * 60 * 1000);

    const activeKiosks = kiosks.filter((kiosk) => getKioskDisplayStatus(kiosk) === "ACTIVE");
    const warningKiosks = kiosks.filter((kiosk) => getKioskDisplayStatus(kiosk) === "NEAR_FULL");
    const fullKiosks = kiosks.filter((kiosk) => getKioskDisplayStatus(kiosk) === "FULL");
    const todayReceipts = points.filter((point) => isBetween(point.timestamp, todayStart, todayEnd));
    const todayMembers = members.filter((member) => isBetween(member.timestamp, todayStart, todayEnd));
    const recentMembers = members.filter((member) => member.timestamp >= sevenDaysAgo);
    const pendingWithdrawAmount = withdraws.reduce((sum, point) => sum + Math.abs(Number(point.pointDelta || 0)), 0);
    const todayInputAmt = todayReceipts.reduce((sum, point) => sum + Number(point.inputAmt || 0), 0);
    const todayPointDelta = todayReceipts.reduce((sum, point) => sum + Number(point.pointDelta || 0), 0);
    const todayCarbonKg = (todayInputAmt * CARBON_REDUCTION_GRAM_PER_INPUT) / 1000;
    const trendDays = enumerateDays(6);
    const receiptTrend = toDailySeries(trendDays, points, "timestamp");
    const memberTrend = toDailySeries(trendDays, members, "timestamp");
    const partnerRank = getPartnerRank(partner, members);

    const urgentTasks = [
        {
            title: "등록 대기 키오스크",
            value: `${pendings.length}건`,
            description: "설치 정보 확인 후 운영 상태로 전환이 필요합니다.",
            tone: pendings.length > 0 ? "warning" : "normal",
            to: "/",
        },
        {
            title: "수거 임박 키오스크",
            value: `${warningKiosks.length}대`,
            description: "투입량이 50% 이상인 키오스크입니다.",
            tone: warningKiosks.length > 0 ? "warning" : "normal",
            to: "/",
        },
        {
            title: "수거 필요 키오스크",
            value: `${fullKiosks.length}대`,
            description: "투입량이 80% 이상이거나 가득 찬 키오스크입니다.",
            tone: fullKiosks.length > 0 ? "danger" : "normal",
            to: "/",
        },
        {
            title: "환전 지급 대기",
            value: `${withdraws.length}건`,
            description: `대기 금액 ₩${pendingWithdrawAmount.toLocaleString()}`,
            tone: withdraws.length > 0 ? "warning" : "normal",
            to: "/point",
        },
    ];

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <div className={style["hero"]}>
                        <div>
                            <h4 className={common["text--title"]}>대시보드</h4>
                            <p className={common["text--copy"]}>
                                키오스크, 포인트, 회원, 파트너사 데이터를 기준으로 운영 상태를 요약합니다.
                            </p>
                        </div>
                        <div className={style["hero-actions"]}>
                            <Link className={style["button--secondary"]} to="/stats">
                                <BarChart3 size={16} />
                                통계 보기
                            </Link>
                            <Link className={style["button--primary"]} to="/point">
                                <Coins size={16} />
                                포인트 관리
                            </Link>
                        </div>
                    </div>

                    <div className={style["summary-grid"]}>
                        <MetricCard title="전체 키오스크" value={kiosks.length.toLocaleString()} unit="대" icon={<MapPin size={20} />} />
                        <MetricCard
                            title="운영중"
                            value={activeKiosks.length.toLocaleString()}
                            unit="대"
                            icon={<CheckCircle2 size={20} />}
                            tone="success"
                        />
                        <MetricCard
                            title="수거 임박"
                            value={warningKiosks.length.toLocaleString()}
                            unit="대"
                            icon={<AlertTriangle size={20} />}
                            tone="warning"
                        />
                        <MetricCard
                            title="수거 필요"
                            value={fullKiosks.length.toLocaleString()}
                            unit="대"
                            icon={<AlertTriangle size={20} />}
                            tone="danger"
                        />
                        <MetricCard
                            title="환전 대기"
                            value={withdraws.length.toLocaleString()}
                            unit="건"
                            icon={<Coins size={20} />}
                            caption={`₩${pendingWithdrawAmount.toLocaleString()}`}
                            tone="warning"
                        />
                    </div>

                    <div className={style["layout-grid"]}>
                        <section className={style["panel"]}>
                            <PanelHeader kicker="업무 큐" title="처리 필요 업무" description="대기 상태의 주요 운영 업무입니다." />
                            <div className={style["task-list"]}>
                                {urgentTasks.map((task) => (
                                    <TaskCard key={task.title} {...task} />
                                ))}
                            </div>
                        </section>

                        <section className={style["panel"]}>
                            <PanelHeader kicker="오늘 요약" title="오늘 운영 현황" description={formatDate(new Date())} />
                            <div className={style["today-grid"]}>
                                <MiniMetric label="회수 건수" value={`${todayReceipts.length.toLocaleString()}건`} />
                                <MiniMetric label="회수량" value={todayInputAmt.toLocaleString()} />
                                <MiniMetric label="포인트 변동" value={todayPointDelta.toLocaleString()} />
                                <MiniMetric
                                    label="탄소 절감"
                                    value={`${todayCarbonKg.toLocaleString("ko-KR", { maximumFractionDigits: 2 })}kg`}
                                />
                                <MiniMetric label="신규 회원" value={`${todayMembers.length.toLocaleString()}명`} />
                                <MiniMetric label="수거 임박 키오스크" value={`${warningKiosks.length.toLocaleString()}대`} />
                            </div>
                        </section>
                    </div>

                    <div className={style["layout-grid"]}>
                        <section className={style["panel"]}>
                            <PanelHeader kicker="최근 7일" title="회수 건수 추이" description="일자별 회수 기록 수" />
                            <div className={style["chart-body"]}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={receiptTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip formatter={(value: any) => `${value}건`} />
                                        <Bar dataKey="value" name="회수 건수" fill="#61A6E6" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </section>

                        <section className={style["panel"]}>
                            <PanelHeader kicker="최근 7일" title="신규 회원 추이" description={`${recentMembers.length}명 신규 가입`} />
                            <div className={style["chart-body"]}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={memberTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                        <Tooltip formatter={(value: any) => `${value}명`} />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            name="신규 회원"
                                            stroke="#2563eb"
                                            strokeWidth={2}
                                            dot={{ r: 3 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </section>
                    </div>

                    <div className={style["layout-grid"]}>
                        <section className={style["panel"]}>
                            <PanelHeader kicker="바로가기" title="빠른 작업" description="자주 쓰는 운영 메뉴로 이동합니다." />
                            <div className={style["quick-grid"]}>
                                <QuickLink icon={<MapPin size={17} />} label="키오스크 관리" to="/" />
                                <QuickLink icon={<Coins size={17} />} label="포인트 지급" to="/point" />
                                <QuickLink icon={<FileText size={17} />} label="공지 등록" to="/notice" />
                                <QuickLink icon={<FileSpreadsheet size={17} />} label="엑셀 출력" to="/excel" />
                                <QuickLink icon={<BarChart3 size={17} />} label="통계 보기" to="/stats" />
                                <QuickLink icon={<Building2 size={17} />} label="파트너사" to="/partner" />
                            </div>
                        </section>

                        <section className={style["panel"]}>
                            <PanelHeader
                                kicker="파트너사"
                                title="파트너사별 회원 현황"
                                description={`전체 ${partner.length}개 파트너사 · ${members.length}명`}
                            />
                            <div className={style["rank-list"]}>
                                {partnerRank.length === 0 && <div className={style["empty-state"]}>표시할 파트너사 데이터가 없습니다.</div>}
                                {partnerRank.map((item) => (
                                    <PartnerRankRow key={item.id} item={item} max={partnerRank[0]?.count ?? 0} />
                                ))}
                            </div>
                        </section>
                    </div>

                    <section className={style["panel"]}>
                        <PanelHeader kicker="최근 요청" title="환전 지급 대기 목록" description="최신 요청 5건을 표시합니다." />
                        <div className={style["withdraw-list"]}>
                            {withdraws.length === 0 && <div className={style["empty-state"]}>대기 중인 환전 요청이 없습니다.</div>}
                            {withdraws
                                .slice()
                                .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
                                .slice(0, 5)
                                .map((point) => (
                                    <WithdrawRow key={point.id} point={point} />
                                ))}
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

const MetricCard = ({
    title,
    value,
    unit,
    caption,
    icon,
    tone = "default",
}: {
    title: string;
    value: string;
    unit: string;
    caption?: string;
    icon: React.ReactNode;
    tone?: "default" | "success" | "danger" | "warning";
}) => (
    <div className={[style["metric-card"], style[`metric-card--${tone}`]].join(" ")}>
        <div>
            <p>{title}</p>
            <strong>
                {value}
                <span>{unit}</span>
            </strong>
            {caption && <small>{caption}</small>}
        </div>
        <div className={style["metric-icon"]}>{icon}</div>
    </div>
);

const PanelHeader = ({ kicker, title, description }: { kicker: string; title: string; description: string }) => (
    <div className={style["panel-header"]}>
        <div>
            <p className={style["section-kicker"]}>{kicker}</p>
            <h5>{title}</h5>
            <p>{description}</p>
        </div>
    </div>
);

const TaskCard = ({
    title,
    value,
    description,
    tone,
    to,
}: {
    title: string;
    value: string;
    description: string;
    tone: string;
    to: string;
}) => (
    <Link className={[style["task-card"], style[`task-card--${tone}`]].join(" ")} to={to}>
        <div>
            <p>{title}</p>
            <strong>{value}</strong>
            <span>{description}</span>
        </div>
        <ArrowRight size={17} />
    </Link>
);

const MiniMetric = ({ label, value }: { label: string; value: string }) => (
    <div className={style["mini-metric"]}>
        <p>{label}</p>
        <strong>{value}</strong>
    </div>
);

const QuickLink = ({ icon, label, to }: { icon: React.ReactNode; label: string; to: string }) => (
    <Link className={style["quick-link"]} to={to}>
        {icon}
        <span>{label}</span>
    </Link>
);

const PartnerRankRow = ({ item, max }: { item: PartnerRank; max: number }) => {
    const ratio = max > 0 ? Math.max((item.count / max) * 100, 8) : 0;

    return (
        <div className={style["rank-row"]}>
            <div className={style["rank-row-head"]}>
                <strong>{item.name}</strong>
                <span>{item.count.toLocaleString()}명</span>
            </div>
            <div className={style["rank-track"]}>
                <div style={{ width: `${ratio}%` }} />
            </div>
        </div>
    );
};

const WithdrawRow = ({ point }: { point: PointType }) => (
    <div className={style["withdraw-row"]}>
        <div>
            <strong>{point.member?.name ?? "이름 없음"}</strong>
            <span>{formatPhone(point.member?.phone) || "전화번호 없음"}</span>
        </div>
        <p>{formatDateTime(point.timestamp)}</p>
        <strong className={style["amount"]}>₩{Math.abs(Number(point.pointDelta || 0)).toLocaleString()}</strong>
        <Link className={style["button--secondary"]} to="/point">
            관리
        </Link>
    </div>
);

type PartnerRank = {
    id: string;
    name: string;
    count: number;
};

const getPartnerRank = (partners: PartnerType[], members: MemberType[]): PartnerRank[] => {
    const countMap = members.reduce<Record<string, number>>((acc, member) => {
        if (!member.partnerId) return acc;
        acc[member.partnerId] = (acc[member.partnerId] || 0) + 1;
        return acc;
    }, {});

    return partners
        .map((p) => ({ id: p.id, name: p.name || p.id, count: countMap[p.id] || 0 }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
};

const toDailySeries = <T extends { timestamp?: number }>(days: string[], rows: T[], key: keyof T) => {
    const map: Record<string, number> = {};

    for (const row of rows) {
        const timestamp = row[key] as number | undefined;
        if (!timestamp) continue;
        const day = formatYMD(new Date(timestamp));
        if (days.includes(day)) map[day] = (map[day] || 0) + 1;
    }

    return days.map((day) => ({ date: day.slice(5), value: map[day] || 0 }));
};

const enumerateDays = (lookback: number) => {
    const days: string[] = [];
    const date = new Date();
    date.setDate(date.getDate() - lookback);
    date.setHours(0, 0, 0, 0);

    for (let i = 0; i <= lookback; i += 1) {
        days.push(formatYMD(date));
        date.setDate(date.getDate() + 1);
    }

    return days;
};

const isBetween = (timestamp: number | undefined, start: number, end: number) =>
    typeof timestamp === "number" && timestamp >= start && timestamp <= end;

const getKioskFillRate = (kiosk: KioskType) => {
    const maxCapacity = Number(kiosk.maxCapacity ?? 0);
    const currentCapacity = Number(kiosk.currentCapacity ?? 0);
    return maxCapacity > 0 ? Math.min(Math.round((currentCapacity / maxCapacity) * 100), 100) : 0;
};

const getKioskDisplayStatus = (kiosk: KioskType): KioskDisplayStatus => {
    if (kiosk.status === "PENDING" || kiosk.status === "FULL") return kiosk.status;

    const fillRate = getKioskFillRate(kiosk);
    if (fillRate >= CAPACITY_DANGER_RATE) return "FULL";
    if (fillRate >= CAPACITY_WARNING_RATE) return "NEAR_FULL";
    return kiosk.status;
};

const startOfDay = (ms: number) => {
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const endOfDay = (ms: number) => {
    const d = new Date(ms);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
};

const formatYMD = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const formatDate = (d: Date) => `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;

const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return "-";
    return new Date(timestamp).toLocaleString("ko-KR");
};

const formatPhone = (phone: string | undefined) => {
    const cleaned = ("" + phone).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone || "";
};

export default DashboardV2;
